// Export PDF a CSV.
// (Krok B1b: rozděleno ze src/app.js beze změny kódu; importy/exporty doplněny automaticky.)
import { DELETED_RETENTION_DAYS, ICONS, SCORE_FACTORY_DEFAULT, auditNoLabel, auditNoValue, canManageContent, esc, fmtDate, fmtDateShort, queueWrite, showToast, state, auditAbsentNames, auditPresentNames, auditAuditorLabel } from './state.js';
import { deletedAudits } from './pagination.js';
import { db, downloadsCap } from './boot.js';
import { auditScorePercent, auditUsesOkNok, auditUsesPercent, findingsCountText, scoreThresholdsForType } from '../views/audit-runner.js';
import { filteredHistoryList } from '../views/history.js';
import { CORRECTIVE_STATUS_LABELS, auditCorrectiveSeqMap, buildCorrectiveItem, correctiveCode, correctiveStatusBadgeHtml, findCorrectiveItemByKey, isCorrectiveOverdue } from '../views/corrective.js';
import { render } from '../main-render.js';

// ---------- PDF export ----------
function sanitizeFilename(s){
  var out = String(s||'audit').normalize('NFKD').replace(/[̀-ͯ]/g,'')
    .replace(/[^A-Za-z0-9_\- ]/g,'').trim().replace(/\s+/g,'_');
  return out || 'audit';
}
// PDF is generated by rendering the audit as real styled HTML (same look as the
// app's history detail screen) off-screen, rasterizing it with html2canvas, and
// slicing that single canvas across A4 pages along block boundaries. This avoids
// jsPDF's built-in fonts entirely, which do not cover Czech diacritics (č š ž ř ě
// ů ď ť ň) and were rendering as garbled characters.
function pdfScorePercentColor(pct, thresholds){
  if(pct==null) return '#8a939d';
  var th = thresholds || state.scoreDefaults || SCORE_FACTORY_DEFAULT;
  return pct>=th.good ? '#1f8a4c' : (pct<th.bad ? '#c22f26' : '#8a7a2e');
}
function pdfBadge(result){
  var map = {OK:['#e3f5ea','#1f8a4c'], NOK:['#fcebe9','#c22f26'], NA:['#f8f1dc','#8a7a2e'], 'Dobrá praxe':['#e3f5ea','#1f8a4c'], 'Neshoda':['#fcebe9','#c22f26']};
  var c = map[result] || ['#edeff2','#5a646e'];
  return '<span style="display:inline-block;font-size:10.5px;font-weight:700;' +
    'padding:2px 9px;border-radius:999px;background:'+c[0]+';color:'+c[1]+';white-space:nowrap">' +
    esc(result || '—') + '</span>';
}
function buildAuditPrintHtml(a){
  var codeSeqs = auditCorrectiveSeqMap(a);
  function seqCode(ansId, subId){ return esc(correctiveCode(a, codeSeqs[subId ? ansId+'::'+subId : ansId])); }
  // Opatření u otázky: nadpis s ID, na dalším řádku text, pak odpovědná osoba a termín.
  function pdfCorrectiveLines(ca, code){
    return '<div style="font-weight:700">Nápravné opatření'+(code?' '+code:'')+'</div>' +
      '<div style="margin-top:2px;white-space:pre-wrap">'+esc(ca.description||'—')+'</div>' +
      '<div style="margin-top:3px;color:#5a646e">Odpovídá: '+esc(ca.responsible||'—')+' · Termín: '+(ca.dueDate?fmtDateShort(ca.dueDate):'—')+'</div>';
  }
  var FS = "font-family:'IBM Plex Sans',Arial,sans-serif;";
  var html = '';
  var b = (a.companyId && state.companies.get(a.companyId)) || state.branding || {};
  if(b.companyName || b.name || b.logoUrl || b.ico || b.address){
    if(!b.companyName && b.name) b = Object.assign({}, b, {companyName:b.name});

    html += '<div class="pdf-block" style="'+FS+'padding-bottom:12px;display:flex;align-items:center;gap:10px;border-bottom:1px solid #e3e6ea">';
    if(b.logoUrl){
      var logoPdfH = Math.round((b.logoHeight||32) * 1.1);
      html += '<img src="'+esc(b.logoUrl)+'" style="height:'+logoPdfH+'px;width:auto;max-width:160px;object-fit:contain;flex-shrink:0;display:block"/>';
    }
    html += '<div>';
    if(b.companyName){
      html += '<div style="font-weight:600;font-size:13.5px;color:#1a2230">'+esc(b.companyName)+'</div>';
    }
    var metaLine = [];
    if(b.address) metaLine.push(b.address);
    if(b.ico) metaLine.push('IČO '+b.ico);
    if(b.dic) metaLine.push('DIČ '+b.dic);
    if(metaLine.length){
      html += '<div style="font-size:10.5px;color:#5a646e;margin-top:1px">'+esc(metaLine.join(' · '))+'</div>';
    }
    html += '</div></div>';
  }
  html += '<div class="pdf-block" style="'+FS+'padding-bottom:14px">' +
    '<div style="font-weight:700;font-size:20px;color:#1a2230;margin-bottom:2px">'+esc(a.workplaceName||'Audit')+
    (a.auditNumber!=null ? ' <span style="font-weight:400;color:#8a939d;font-size:15px">'+esc(auditNoLabel(a))+'</span>' : '')+'</div>' +
    '<div style="font-size:13px;color:#5a646e;margin-bottom:12px">'+esc(a.auditTypeName||'Audit')+'</div>' +
    '<div style="border:1px solid #e3e6ea;border-radius:10px;padding:12px 14px;background:#f7f8fa">';
  [['Provozovna', a.branchName||'Sídlo firmy'],['Druh auditu', a.auditTypeName||'—'],['Auditor', auditAuditorLabel(a)]]
   .concat(auditPresentNames(a).length ? [['Zúčastněné osoby', auditPresentNames(a).join(', ')]] : [])
   .concat(auditAbsentNames(a).length ? [['Nepřítomní', auditAbsentNames(a).join(', ')]] : [])
   .concat([['Zahájeno', fmtDate(a.startedAt)],['Uzavřeno', fmtDate(a.closedAt)]]).forEach(function(r){
    html += '<div style="display:flex;justify-content:space-between;font-size:12.5px;padding:3px 0">' +
      '<span style="color:#5a646e">'+esc(r[0])+'</span><span style="font-weight:600;color:#1a2230">'+esc(r[1])+'</span></div>';
  });
  var overallOk = a.summary && a.summary.overall==='OK';
  var hasQuestions = (a.answers||[]).length>0, findings = a.findings || [];
  if(findings.length || !hasQuestions){
    html += '<div style="display:flex;justify-content:space-between;font-size:12.5px;padding:3px 0">' +
      '<span style="color:#5a646e">'+(hasQuestions ? 'Volná zjištění' : 'Zjištění')+'</span><span style="font-weight:600;color:#1a2230">'+esc(findingsCountText(findings))+'</span></div>';
  }
  if(hasQuestions) html += '<div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;padding-top:8px;border-top:1px solid #e3e6ea">' +
    '<span style="font-size:12.5px;color:#5a646e">Výsledek</span>' +
    '<span style="display:inline-flex;align-items:center;gap:8px">' + (auditUsesOkNok(a) ? pdfBadge(overallOk?'OK':'NOK') : '') +
    '<span style="font-size:11px;color:#8a939d;font-variant-numeric:tabular-nums">OK '+(a.summary?a.summary.ok:0)+
    ' · NOK '+(a.summary?a.summary.nok:0)+' · N/A '+(a.summary?a.summary.na:0)+
    (auditUsesPercent(a) && auditScorePercent(a)!=null ? ' · <b style="color:'+pdfScorePercentColor(auditScorePercent(a), scoreThresholdsForType(a.auditTypeId))+'">'+auditScorePercent(a)+' %</b>' : '') +
    '</span></span></div>';
  html += '</div></div>';

  var byGroup = new Map(), order = [];
  (a.answers||[]).forEach(function(ans){
    if(!byGroup.has(ans.groupId)){ byGroup.set(ans.groupId,{name:ans.groupName,items:[]}); order.push(ans.groupId); }
    byGroup.get(ans.groupId).items.push(ans);
  });
  order.forEach(function(gid){
    var grp = byGroup.get(gid);
    html += '<div class="pdf-block" style="'+FS+'padding:10px 0 6px">' +
      '<div style="font-weight:700;font-size:13.5px;color:#1a2230">'+esc(grp.name)+'</div></div>';
    grp.items.forEach(function(ans){
      html += '<div class="pdf-block" style="'+FS+'padding:8px 0;border-bottom:1px solid #eef0f2">';
      html += '<div style="display:flex;justify-content:space-between;gap:10px;align-items:flex-start">' +
        '<div style="font-size:12.5px;color:#1a2230;flex:1;line-height:1.4">'+esc(ans.questionText)+'</div>' +
        pdfBadge(ans.result) + '</div>';
      if(ans.note){
        html += '<div style="font-size:11px;color:#5a646e;margin-top:5px;white-space:pre-wrap;line-height:1.4">Zjištění: '+esc(ans.note)+'</div>';
      }
      if(ans.result==='NOK' && ans.correctiveAction && (ans.correctiveAction.description||ans.correctiveAction.responsible||ans.correctiveAction.dueDate)){
        var ca = ans.correctiveAction;
        html += '<div style="font-size:11px;margin-top:6px;padding:6px 8px;border-radius:6px;background:#fff7e0;color:#1a2230;line-height:1.4">' +
          pdfCorrectiveLines(ca, seqCode(ans.id)) + '</div>';
      }
      if(ans.attachments && ans.attachments.length){
        // The <img> here gets no width/height yet: html2canvas does not reliably honor
        // object-fit, so it would stretch/distort the photo to fill the box. Instead,
        // renderHtmlBlocksIntoDoc measures each photo's real aspect ratio once it has
        // loaded and sets explicit pixel dimensions that fit inside this box, which
        // html2canvas rasterizes correctly because there is no CSS fitting involved.
        html += '<div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">' + ans.attachments.map(function(att){
          return '<div class="pdf-photo-box" style="flex:0 0 45%;min-width:140px;max-width:260px;height:180px;border:1px solid #e3e6ea;' +
            'border-radius:8px;overflow:hidden;background:#f7f8fa;display:flex;align-items:center;justify-content:center">' +
            '<img class="pdf-photo-img" src="'+att.url+'" style="display:block;max-width:100%;max-height:100%"/></div>';
        }).join('') + '</div>';
      }
      if(ans.subAnswers && ans.subAnswers.length){
        html += '<div style="margin:8px 0 0 6px;padding-left:10px;border-left:2px solid #e3e6ea">' + ans.subAnswers.map(function(sa){
          var row = '<div style="display:flex;justify-content:space-between;gap:10px;padding:4px 0;font-size:12.5px;color:#1a2230;align-items:center">' +
            '<span style="line-height:1.35">'+esc(sa.text)+'</span>' + pdfBadge(sa.result) + '</div>';
          if(sa.note){
            row += '<div style="font-size:11px;color:#5a646e;margin-top:2px;white-space:pre-wrap;line-height:1.4">Zjištění: '+esc(sa.note)+'</div>';
          }
          if(sa.result==='NOK' && sa.correctiveAction && (sa.correctiveAction.description||sa.correctiveAction.responsible||sa.correctiveAction.dueDate)){
            var sca = sa.correctiveAction;
            row += '<div style="font-size:11px;margin:4px 0 6px;padding:6px 8px;border-radius:6px;background:#fff7e0;color:#1a2230;line-height:1.4">' +
              pdfCorrectiveLines(sca, seqCode(ans.id, sa.id)) + '</div>';
          }
          if(sa.attachments && sa.attachments.length){
            row += '<div style="display:flex;gap:6px;margin:4px 0 6px;flex-wrap:wrap">' + sa.attachments.map(function(att){
              return '<div class="pdf-photo-box" style="flex:0 0 30%;min-width:100px;max-width:200px;height:120px;border:1px solid #e3e6ea;' +
                'border-radius:8px;overflow:hidden;background:#f7f8fa;display:flex;align-items:center;justify-content:center">' +
                '<img class="pdf-photo-img" src="'+att.url+'" style="display:block;max-width:100%;max-height:100%"/></div>';
            }).join('') + '</div>';
          }
          return row;
        }).join('') + '</div>';
      }
      html += '</div>';
    });
  });

  // ---- Volná zjištění (neshody a dobrá praxe) ----
  if(findings.length){
    html += '<div class="pdf-block" style="'+FS+'padding:10px 0 6px">' +
      '<div style="font-weight:700;font-size:13.5px;color:#1a2230">'+(hasQuestions ? 'Volná zjištění' : 'Zjištění')+'</div></div>';
    findings.forEach(function(f, i){
      var good = f.result==='GOOD';
      html += '<div class="pdf-block" style="'+FS+'padding:8px 0;border-bottom:1px solid #eef0f2">' +
        '<div style="display:flex;justify-content:space-between;gap:10px;align-items:flex-start">' +
        '<div style="font-size:12.5px;color:#1a2230;flex:1;line-height:1.4"><b>Zjištění '+(i+1)+'</b>' +
        (f.workplaceName && f.workplaceId!==a.workplaceId ? ' <span style="color:#5a646e">· '+esc(f.workplaceName)+'</span>' : '') + '</div>' +
        pdfBadge(good ? 'Dobrá praxe' : 'Neshoda') + '</div>';
      if(f.note) html += '<div style="font-size:11.5px;color:#1a2230;margin-top:5px;white-space:pre-wrap;line-height:1.4">'+esc(f.note)+'</div>';
      if(!good && f.correctiveAction){
        html += '<div style="font-size:11px;margin-top:6px;padding:6px 8px;border-radius:6px;background:#fff7e0;color:#1a2230;line-height:1.4">' +
          pdfCorrectiveLines(f.correctiveAction, seqCode(f.id)) + '</div>';
      }
      if(f.attachments && f.attachments.length){
        html += '<div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap">' + f.attachments.map(function(att){
          return '<div class="pdf-photo-box" style="flex:0 0 45%;min-width:140px;max-width:260px;height:180px;border:1px solid #e3e6ea;' +
            'border-radius:8px;overflow:hidden;background:#f7f8fa;display:flex;align-items:center;justify-content:center">' +
            '<img class="pdf-photo-img" src="'+att.url+'" style="display:block;max-width:100%;max-height:100%"/></div>';
        }).join('') + '</div>';
      }
      html += '</div>';
    });
  }

  // ---- Souhrnná tabulka všech NOK (otázky i podotázky) na konci reportu ----
  // Pořadí stejné jako v auditu (podle byGroup/order výše), takže se dá použít
  // jako akční plán bez listování celým auditem.
  var nokRowsHtml = '';
  function nokRow(groupName, questionText, parentContext, result, ca, note, attachments, code){
    var row = '<div class="pdf-block" style="'+FS+'display:flex;gap:8px;font-size:10.5px;color:#1a2230;padding:7px 0;border-bottom:1px solid #eef0f2;align-items:flex-start">' +
      '<div style="flex:0 0 17%;line-height:1.35">'+esc(groupName)+'</div>' +
      '<div style="flex:0 0 23%;line-height:1.35">'+esc(questionText) +
      (parentContext ? '<div style="font-size:9px;color:#8a939d;margin-top:2px">'+esc(parentContext)+'</div>' : '') + '</div>' +
      '<div style="flex:1;line-height:1.35">' + (code ? '<b>'+esc(code)+'</b> ' : '') + esc((ca && ca.description) || '—') +
      (note ? '<div style="font-size:9.5px;color:#5a646e;margin-top:3px;white-space:pre-wrap">Zjištění: '+esc(note)+'</div>' : '') +
      ((attachments && attachments.length) ? ('<div style="display:flex;gap:5px;margin-top:4px;flex-wrap:wrap">' + attachments.map(function(att){
        return '<div class="pdf-photo-box" style="flex:0 0 60px;width:60px;height:46px;border:1px solid #e3e6ea;' +
          'border-radius:6px;overflow:hidden;background:#f7f8fa;display:flex;align-items:center;justify-content:center">' +
          '<img class="pdf-photo-img" src="'+att.url+'" style="display:block;max-width:100%;max-height:100%"/></div>';
      }).join('') + '</div>') : '') + '</div>' +
      '<div style="flex:0 0 14%;line-height:1.35">'+esc((ca && ca.responsible) || '—')+'</div>' +
      '<div style="flex:0 0 10%;line-height:1.35">'+((ca && ca.dueDate) ? fmtDateShort(ca.dueDate) : '—')+'</div>' +
      '</div>';
    nokRowsHtml += row;
  }
  order.forEach(function(gid){
    var grp = byGroup.get(gid);
    grp.items.forEach(function(ans){
      if(ans.result==='NOK'){
        nokRow(grp.name, ans.questionText, null, ans.result, ans.correctiveAction, ans.note, ans.attachments, seqCode(ans.id));
      }
      (ans.subAnswers||[]).forEach(function(sa){
        if(sa.result==='NOK'){
          nokRow(grp.name, sa.text, 'nadřazená otázka: '+ans.questionText, sa.result, sa.correctiveAction, sa.note, sa.attachments, seqCode(ans.id, sa.id));
        }
      });
    });
  });
  findings.forEach(function(f){
    if(f.result==='NOK') nokRow('Volné zjištění', f.workplaceName||'', null, f.result, f.correctiveAction, f.note, f.attachments, seqCode(f.id));
  });
  if(nokRowsHtml){
    html += '<div class="pdf-block" style="'+FS+'padding:18px 0 8px">' +
      '<div style="font-weight:700;font-size:15px;color:#1a2230">'+(hasQuestions ? 'Přehled NOK a nápravných opatření' : 'Přehled neshod a nápravných opatření')+'</div></div>';
    html += '<div class="pdf-block" style="'+FS+'display:flex;gap:8px;font-size:9.5px;font-weight:700;color:#5a646e;border-bottom:2px solid #1a2230;padding:4px 0 6px">' +
      '<div style="flex:0 0 17%">Skupina</div>' +
      '<div style="flex:0 0 23%">Otázka / podotázka</div>' +
      '<div style="flex:1">Nápravné opatření</div>' +
      '<div style="flex:0 0 14%">Odpovídá</div>' +
      '<div style="flex:0 0 10%">Termín</div>' +
      '</div>';
    html += nokRowsHtml;
  }

  return html;
}
async function renderAuditIntoDoc(doc, a, startNewPage){
  return renderHtmlBlocksIntoDoc(doc, buildAuditPrintHtml(a), startNewPage);
}
// Generic renderer: rasterizes an HTML string (its top-level .pdf-block
// elements mark page-break boundaries) and paginates it onto an existing
// jsPDF doc. Used by the single/bulk audit PDF export.
async function renderHtmlBlocksIntoDoc(doc, html, startNewPage){
  var h2c = window.html2canvas;
  if(!h2c) throw new Error('html2canvas not loaded');

  var PRINT_W = 720;
  var wrap = document.createElement('div');
  wrap.style.position = 'fixed';
  wrap.style.left = '-10000px';
  wrap.style.top = '0';
  wrap.style.width = PRINT_W + 'px';
  wrap.style.background = '#ffffff';
  wrap.innerHTML = html;

  // html2canvas (useCORS) re-fetches every image in CORS mode. In WebKit
  // (iOS Safari and Chrome) that replaces the memory-cache entry the app's
  // own non-CORS <img> elements use for the same URL, and the app's photos
  // and logos go blank until the view is rebuilt. Giving the PDF copies a
  // distinct URL (Supabase Storage ignores the extra param) and loading them
  // as CORS up front keeps the two cache entries apart.
  var imgs = Array.from(wrap.querySelectorAll('img'));
  imgs.forEach(function(img){
    var src = img.getAttribute('src') || '';
    if(!/^https?:/i.test(src)) return;
    img.crossOrigin = 'anonymous';
    img.src = src + (src.indexOf('?')>=0 ? '&' : '?') + 'pdf=1';
  });
  document.body.appendChild(wrap);

  await Promise.all(imgs.map(function(img){
    return new Promise(function(res){
      if(img.complete && img.naturalWidth>0){ res(); return; }
      img.onload = res; img.onerror = res;
      setTimeout(res, 4000);
    });
  }));

  // html2canvas does not reliably honor CSS object-fit, so photo boxes (.pdf-photo-box)
  // are sized here in JS from each image's real aspect ratio instead — fitting it inside
  // the box (like object-fit:contain) with explicit pixel width/height, which html2canvas
  // rasterizes faithfully since no CSS-level fitting is involved.
  Array.from(wrap.querySelectorAll('.pdf-photo-img')).forEach(function(img){
    if(!img.naturalWidth || !img.naturalHeight) return;
    var box = img.parentElement;
    var bw = box.clientWidth, bh = box.clientHeight;
    if(!bw || !bh) return;
    var fit = Math.min(bw/img.naturalWidth, bh/img.naturalHeight);
    img.style.width = Math.round(img.naturalWidth*fit) + 'px';
    img.style.height = Math.round(img.naturalHeight*fit) + 'px';
  });

  var scale = 2;
  var blocks = Array.from(wrap.querySelectorAll('.pdf-block')).map(function(el){
    return {y: Math.round(el.offsetTop*scale), h: Math.round(el.offsetHeight*scale)};
  });
  var fullCanvas;
  try{
    // Pojistka: kdyby html2canvas na něco čekal donekonečna (Safari),
    // export skončí chybou místo věčného „Generuji…“.
    // Do kopie stránky (html2canvas ji staví v iframe) jen hlava a obsah PDF —
    // ne celá aplikace: WebKit čeká na načtení každého obrázku v kopii
    // (i líně načítaných, které se ve skrytém iframe nenačtou nikdy).
    var keepForPdf = function(el){
      if(el===wrap || wrap.contains(el) || el.contains(wrap)) return true;
      var tag = el.tagName;
      return tag==='HEAD' || tag==='STYLE' || tag==='LINK' || tag==='META' || tag==='TITLE' || (el.parentNode && el.parentNode.tagName==='HEAD');
    };
    fullCanvas = await Promise.race([
      h2c(wrap, {scale:scale, useCORS:true, backgroundColor:'#ffffff', windowWidth:PRINT_W, ignoreElements:function(el){ return !keepForPdf(el); }}),
      new Promise(function(_, reject){ setTimeout(function(){ reject(new Error('PDF: vypršel čas vykreslení')); }, 90000); })
    ]);
  } finally {
    document.body.removeChild(wrap);
  }

  var pageW=210, pageH=297, marginX=14, marginY=14;
  var contentWmm = pageW - marginX*2;
  var pxPerMm = fullCanvas.width / contentWmm;
  if(startNewPage) doc.addPage();
  var curY = marginY;

  function drawSlice(srcY, srcH){
    var c = document.createElement('canvas');
    c.width = fullCanvas.width;
    c.height = srcH;
    c.getContext('2d').drawImage(fullCanvas, 0, srcY, fullCanvas.width, srcH, 0, 0, fullCanvas.width, srcH);
    var data = c.toDataURL('image/jpeg', 0.93);
    c.width = c.height = 0; // release canvas memory right away (iOS has a hard cap)
    var hmm = srcH / pxPerMm;
    doc.addImage(data, 'JPEG', marginX, curY, contentWmm, hmm);
    curY += hmm;
  }

  blocks.forEach(function(b, idx){
    var hmm = b.h / pxPerMm;
    var maxHmm = pageH - marginY*2;
    if(idx>0 && hmm <= maxHmm + 0.5 && curY + hmm > pageH - marginY + 0.5){
      doc.addPage();
      curY = marginY;
    }
    if(hmm > maxHmm + 0.5){
      var rem = b.h, sy = b.y;
      while(rem>0){
        if(curY >= pageH - marginY - 2){ doc.addPage(); curY = marginY; }
        var availPx = Math.floor((pageH - marginY - curY) * pxPerMm);
        var take = Math.max(1, Math.min(availPx, rem));
        drawSlice(sy, take);
        sy += take; rem -= take;
      }
    } else {
      drawSlice(b.y, b.h);
    }
    curY += 1.5;
  });
  // iOS WebKit only frees canvas backing memory once it's shrunk to 0x0;
  // left as is, a big export exhausts it and the app's own photos/logos
  // stop painting until the screen is rebuilt.
  fullCanvas.width = fullCanvas.height = 0;
}
// Official app logo in every generated PDF's footer — just the "BOZP-SOFTWARE"
// wordmark, not the cube graphic or the "MODULÁRNÍ SYSTÉM ŘÍZENÍ" tagline:
// the rest of this PDF pipeline renders HTML through html2canvas specifically
// to get Czech diacritics right (jsPDF's built-in fonts don't have č/š/ž/ř/ě),
// but the wordmark itself is plain ASCII, so drawing it directly with jsPDF's
// own text/line primitives (not another html2canvas pass) is simplest here —
// it just has to run once, after pagination, over however many pages ended up
// in the doc.
// Patička PDF = logo z jediného zdroje (window.APP_LOGO_SVG) převedené na
// PNG; když převod selže, zůstane textový nápis BOZP-SOFTWARE.
var appLogoPngPromise = null;
function appLogoPngForPdf(){
  if(appLogoPngPromise) return appLogoPngPromise;
  appLogoPngPromise = new Promise(function(resolve){
    try{
      var svg = window.appLogoHtml({text:'#0F172A', gap:'#FFFFFF'}).replace(/ style="[^"]*"/, ' width="840" height="220"');
      var img = new Image();
      img.onload = function(){
        try{
          var c = document.createElement('canvas'); c.width = 840; c.height = 220;
          c.getContext('2d').drawImage(img, 0, 0, 840, 220);
          var url = c.toDataURL('image/png');
          c.width = c.height = 0;
          resolve(url);
        }catch(e){ resolve(null); }
      };
      img.onerror = function(){ resolve(null); };
      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    }catch(e){ resolve(null); }
  }).then(function(url){ if(!url) appLogoPngPromise = null; return url; });
  return appLogoPngPromise;
}
async function addBrandFooterToAllPages(doc){
  var pageW = 210, pageH = 297;
  var png = await appLogoPngForPdf();
  var totalPages = doc.getNumberOfPages();
  for(var i=1;i<=totalPages;i++){
    doc.setPage(i);
    var y=pageH-8;
    doc.setDrawColor(224,224,224);
    doc.setLineWidth(0.2);
    doc.line(14, y-4, pageW-14, y-4);
    // Číslo stránky vpravo v zápatí (jen ASCII → stačí vestavěné písmo jsPDF).
    doc.setFont('helvetica','normal');
    doc.setFontSize(7.5);
    doc.setTextColor(120,128,140);
    doc.text('Strana '+i+' z '+totalPages, pageW-14, y+1.3, {align:'right'});
    if(png){
      var h = 7, w = h*840/220;
      doc.addImage(png, 'PNG', (pageW-w)/2, y-2.6, w, h);
      continue;
    }
    doc.setFont('helvetica','bold');
    doc.setFontSize(8);
    var segBOZP='BOZP', segDash='-', segSOFT='SOFTWARE';
    var wB=doc.getTextWidth(segBOZP), wD=doc.getTextWidth(segDash), wS=doc.getTextWidth(segSOFT);
    var x=(pageW-(wB+wD+wS))/2;
    doc.setTextColor(15,23,42);
    doc.text(segBOZP, x, y); x+=wB;
    doc.text(segDash, x, y); x+=wD;
    doc.setTextColor(16,185,129);
    doc.text(segSOFT, x, y);
  }
}
// jsPDF + html2canvas (~350 kB) are only needed for PDF export, so they're
// loaded on first use instead of blocking the initial page load.
var PDF_LIB_URLS = [
  'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
  'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
];
var pdfLibsPromise = null;
function loadScript(src){
  return new Promise(function(resolve, reject){
    var el = document.createElement('script');
    el.src = src;
    el.onload = resolve;
    el.onerror = function(){ el.remove(); reject(new Error('Nepodařilo se načíst ' + src)); };
    document.head.appendChild(el);
  });
}
function ensurePdfLibs(){
  if(window.jspdf && window.html2canvas) return Promise.resolve();
  if(!pdfLibsPromise){
    pdfLibsPromise = Promise.all(PDF_LIB_URLS.map(loadScript))
      .catch(function(err){ pdfLibsPromise = null; throw err; }); // allow retry (e.g. after going back online)
  }
  return pdfLibsPromise;
}
async function buildAuditPdfBlob(a){
  await ensurePdfLibs();
  var jsPDFCtor = window.jspdf && window.jspdf.jsPDF;
  if(!jsPDFCtor) throw new Error('jsPDF not loaded');
  var doc = new jsPDFCtor({unit:'mm', format:'a4'});
  await renderAuditIntoDoc(doc, a, false);
  await addBrandFooterToAllPages(doc);
  return doc.output('blob');
}
// Single-corrective-action PDF: a short, standalone document for one
// opatření (its question/subquestion context, description, responsible
// person, due date, status and the audit photos), independent of the
// full audit PDF export above. Reuses the same rasterize-and-paginate
// pipeline (renderHtmlBlocksIntoDoc) and pdf-block/pdf-photo-box classes.
function buildCorrectiveItemPrintHtml(item){
  var FS = "font-family:'IBM Plex Sans',Arial,sans-serif;";
  var html = '';
  var audit = state.audits.get(item.auditId);
  var b = (audit && audit.companyId && state.companies.get(audit.companyId)) || state.branding || {};
  if(b.companyName || b.name || b.logoUrl || b.ico || b.address){
    if(!b.companyName && b.name) b = Object.assign({}, b, {companyName:b.name});
    html += '<div class="pdf-block" style="'+FS+'padding-bottom:12px;display:flex;align-items:center;gap:10px;border-bottom:1px solid #e3e6ea">';
    if(b.logoUrl){
      var logoPdfH = Math.round((b.logoHeight||32) * 1.1);
      html += '<img src="'+esc(b.logoUrl)+'" style="height:'+logoPdfH+'px;width:auto;max-width:160px;object-fit:contain;flex-shrink:0;display:block"/>';
    }
    html += '<div>';
    if(b.companyName){
      html += '<div style="font-weight:600;font-size:13.5px;color:#1a2230">'+esc(b.companyName)+'</div>';
    }
    var metaLine = [];
    if(b.address) metaLine.push(b.address);
    if(b.ico) metaLine.push('IČO '+b.ico);
    if(b.dic) metaLine.push('DIČ '+b.dic);
    if(metaLine.length){
      html += '<div style="font-size:10.5px;color:#5a646e;margin-top:1px">'+esc(metaLine.join(' · '))+'</div>';
    }
    html += '</div></div>';
  }

  var overdue = isCorrectiveOverdue(item);
  html += '<div class="pdf-block" style="'+FS+'padding-bottom:14px">' +
    '<div style="font-weight:700;font-size:18px;color:#1a2230;margin-bottom:2px">Nápravné opatření'+(item.code?' <span style="font-weight:600;color:#1f4e8c">'+esc(item.code)+'</span>':'')+'</div>' +
    '<div style="font-size:13px;color:#5a646e;margin-bottom:12px">'+esc(item.workplaceName||'')+' · '+esc(item.auditTypeName||'Audit')+'</div>' +
    '<div style="border:1px solid #e3e6ea;border-radius:10px;padding:12px 14px;background:#f7f8fa">';
  (item.code ? [['ID opatření', item.code]] : []).concat(item.auditNumber!=null ? [['Číslo auditu', auditNoLabel(item)]] : []).concat([
   ['Provozovna', item.branchName||'Sídlo firmy'],
   ['Otázka', item.isFinding ? 'Volné zjištění (bez otázky)' : (item.parentQuestionText ? item.parentQuestionText+' → ' : '') + (item.questionText||'')],
   ['Odpovědná osoba', item.responsible||'—'],
   ['Auditor', item.auditorName||'—'],
   ['Datum auditu', fmtDate(item.closedAt)]
  ]).forEach(function(r){
    html += '<div style="display:flex;justify-content:space-between;gap:10px;font-size:12.5px;padding:3px 0">' +
      '<span style="color:#5a646e;flex-shrink:0">'+esc(r[0])+'</span><span style="font-weight:600;color:#1a2230;text-align:right">'+esc(r[1])+'</span></div>';
  });
  html += '<div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;padding-top:8px;border-top:1px solid #e3e6ea">' +
    '<span style="font-size:12.5px;color:#5a646e">Termín</span>' +
    '<span style="'+(overdue?'color:#c22f26;font-weight:700':'font-weight:600;color:#1a2230')+';font-size:12.5px">'+(item.dueDate?fmtDateShort(item.dueDate):'—')+(overdue?' · po termínu':'')+'</span></div>';
  html += '<div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px">' +
    '<span style="font-size:12.5px;color:#5a646e">Stav opatření</span>' + correctiveStatusBadgeHtml(item.status) + '</div>';
  html += '</div></div>';

  html += '<div class="pdf-block" style="'+FS+'padding-bottom:14px">' +
    '<div style="font-weight:700;font-size:13.5px;color:#1a2230;margin-bottom:6px">Popis opatření</div>' +
    '<div style="font-size:12.5px;color:#1a2230;line-height:1.5;white-space:pre-wrap">'+esc(item.description||'—')+'</div></div>';

  if(item.attachments && item.attachments.length){
    html += '<div class="pdf-block" style="'+FS+'padding-bottom:14px">' +
      '<div style="font-weight:700;font-size:13.5px;color:#1a2230;margin-bottom:8px">Fotky z auditu</div>' +
      '<div style="display:flex;gap:8px;flex-wrap:wrap">' + item.attachments.map(function(att){
        return '<div class="pdf-photo-box" style="flex:0 0 45%;min-width:140px;max-width:260px;height:180px;border:1px solid #e3e6ea;' +
          'border-radius:8px;overflow:hidden;background:#f7f8fa;display:flex;align-items:center;justify-content:center">' +
          '<img class="pdf-photo-img" src="'+att.url+'" style="display:block;max-width:100%;max-height:100%"/></div>';
      }).join('') + '</div></div>';
  }
  return html;
}
async function buildCorrectiveItemPdfBlob(item){
  await ensurePdfLibs();
  var jsPDFCtor = window.jspdf && window.jspdf.jsPDF;
  if(!jsPDFCtor) throw new Error('jsPDF not loaded');
  var doc = new jsPDFCtor({unit:'mm', format:'a4'});
  await renderHtmlBlocksIntoDoc(doc, buildCorrectiveItemPrintHtml(item), false);
  await addBrandFooterToAllPages(doc);
  return doc.output('blob');
}
App.exportCorrectiveItemPdf = async function(key){
  var item = findCorrectiveItemByKey(key);
  if(!item){ showToast('Opatření nenalezeno.'); return; }
  var btn = document.getElementById('correctivePdfBtn');
  if(btn){ btn.disabled = true; btn.textContent = 'Generuji…'; }
  try{
    var blob = await buildCorrectiveItemPdfBlob(item);
    if(!downloadsCap){
      showToast('Stahování souborů není v tomto zobrazení dostupné.');
      return;
    }
    var fname = (item.code ? item.code+'_' : '') + sanitizeFilename(item.workplaceName) + '_opatreni_' + (item.closedAt ? item.closedAt.slice(0,10) : '') + '.pdf';
    var res = await downloadsCap.save({filename: fname, data: blob});
    if(res && res.status==='saved'){ showToast('PDF uloženo.'); }
  }catch(e){
    if(e && e.code==='declined'){ /* viewer cancelled, no toast */ }
    else if(e && e.code==='unavailable'){ showToast('Stahování souborů zde není dostupné.'); }
    else { console.error('corrective pdf export failed', e); showToast('Export do PDF se nezdařil.'); }
  } finally {
    render();
  }
};

App.exportAuditPdf = async function(auditId){
  var a = state.audits.get(auditId);
  if(!a){ showToast('Audit nenalezen.'); return; }
  var btn = document.getElementById('pdfExportBtn');
  if(btn){ btn.disabled = true; btn.textContent = 'Generuji…'; }
  try{
    var blob = await buildAuditPdfBlob(a);
    if(!downloadsCap){
      showToast('Stahování souborů není v tomto zobrazení dostupné.');
      return;
    }
    var fname = (a.auditCode && a.auditNumber!=null ? auditNoValue(a)+'_' : '') + sanitizeFilename(a.workplaceName) + '_' + (a.closedAt ? a.closedAt.slice(0,10) : 'audit') + '.pdf';
    var res = await downloadsCap.save({filename: fname, data: blob});
    if(res && res.status==='saved'){ showToast('PDF uloženo.'); }
  }catch(e){
    if(e && e.code==='declined'){ /* viewer cancelled, no toast */ }
    else if(e && e.code==='unavailable'){ showToast('Stahování souborů zde není dostupné.'); }
    else { console.error('pdf export failed', e); showToast('Export do PDF se nezdařil.'); }
  } finally {
    render();
  }
};

var MONTH_NAMES_CS = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];
// Menu "⋯" v Historii: výběr auditů, export zobrazených (podle filtrů), koš.
function historyMoreMenuHtml(){
  var n = filteredHistoryList().length;
  var items = '<button onclick="App.closeMoreMenus();App.toggleHistorySelectMode()">Vybrat audity…</button>' +
    '<div class="divider"></div>' +
    '<button '+(n?'':'disabled')+' onclick="App.closeMoreMenus();App.exportHistory(\'csv\',\'shown\')">CSV souhrn – zobrazené ('+n+')</button>' +
    '<button '+(n?'':'disabled')+' onclick="App.closeMoreMenus();App.exportHistory(\'csvnok\',\'shown\')">CSV NOK a opatření – zobrazené ('+n+')</button>';
  if(canManageContent()){
    items += '<div class="divider"></div><button onclick="App.closeMoreMenus();App.openTrash()">'+ICONS.trash+' Koš ('+deletedAudits().length+')</button>';
  }
  return '<div class="more-wrap"><button type="button" class="filter-toggle-btn more-btn" title="Další akce" onclick="App.toggleMoreMenu(event, this)">⋯</button>' +
    '<div class="profile-menu" style="display:none">'+items+'</div></div>';
}
App.toggleMoreMenu = function(e, btn){
  if(e) e.stopPropagation();
  var menu = btn.parentNode.querySelector('.profile-menu');
  var open = menu.style.display==='block';
  App.closeMoreMenus();
  menu.style.display = open ? 'none' : 'block';
  if(!open){
    // Menu se vejde na obrazovku (12px od okrajů), i když je tlačítko u kraje.
    menu.style.left = '0px';
    var wrapLeft = btn.parentNode.getBoundingClientRect().left, w = menu.offsetWidth;
    var left = Math.min(Math.max(wrapLeft, 12), window.innerWidth - 12 - w);
    menu.style.left = (left - wrapLeft) + 'px';
  }
};
App.closeMoreMenus = function(){
  document.querySelectorAll('.more-wrap .profile-menu').forEach(function(m){ m.style.display='none'; });
};
document.addEventListener('click', function(e){
  if(!(e.target.closest && e.target.closest('.more-wrap'))) App.closeMoreMenus();
});
// Klik mimo panel Filtrů ho zavře (Přehled, Historie, Opatření, Plán).
// Klik uvnitř panelu nebo v liště nad ním (hledání, Řadit, ⋯, Filtry)
// ho nechá otevřený; posouvání prstem klik nevyvolá. Prvek, na který se
// kliklo, svou akci provede (zavření se jen přidá). Kam se kliklo, se
// pozná už ve fázi capture — akce prvku pak může stránku překreslit.
var FILTER_OPEN_KEYS = ['dashboardFiltersOpen','historyFiltersOpen','correctiveFiltersOpen','scheduleFiltersOpen','smFiltersOpen'];
var filterClosePending = false;
document.addEventListener('click', function(e){
  var t = e.target;
  if(!FILTER_OPEN_KEYS.some(function(k){ return state[k]; })) return;
  if(!t || !t.closest || t.closest('.filter-panel, .filter-toggle-row, .list-toolbar')) return;
  FILTER_OPEN_KEYS.forEach(function(k){ state[k] = false; });
  filterClosePending = true;
}, true);
document.addEventListener('click', function(){
  if(!filterClosePending) return;
  filterClosePending = false;
  // Akce prvku už mohla překreslit stránku (panel pak zmizel sám).
  if(document.querySelector('.filter-panel')) render();
});
App.toggleHistorySelectMode = function(){
  state.historySelectMode = !state.historySelectMode;
  state.historySelectedIds = new Set();
  state.confirmTarget = '';
  render();
};
App.toggleHistorySelected = function(id){
  if(state.historySelectedIds.has(id)) state.historySelectedIds.delete(id);
  else state.historySelectedIds.add(id);
  render();
};
App.historySelectAllShown = function(on){
  state.historySelectedIds = new Set(on ? filteredHistoryList().map(function(e){ return e[0]; }) : []);
  render();
};
App.deleteSelectedAudits = function(){
  if(!db || !canManageContent()) return;
  var ids = Array.from(state.historySelectedIds), deletedAt = new Date().toISOString();
  ids.forEach(function(id){
    var a = state.audits.get(id);
    if(a) a.deletedAt = deletedAt;
    queueWrite(function(){ return db.doc('audits/'+id).update({deletedAt: deletedAt}); });
  });
  state.historySelectMode = false; state.historySelectedIds = new Set();
  showToast('Přesunuto do koše: '+ids.length+'. Do '+DELETED_RETENTION_DAYS+' dní je můžete obnovit v Historii → ⋯ → Koš.');
  render();
};
function csvEscape(v){
  var s = (v===null||v===undefined) ? '' : String(v);
  if(/[",;\n]/.test(s)) return '"' + s.replace(/"/g,'""') + '"';
  return s;
}
function csvBlob(rows){
  return new Blob(['﻿' + rows.map(function(r){ return r.map(csvEscape).join(';'); }).join('\r\n')], {type:'text/csv;charset=utf-8'});
}
function historyCsvSummaryRows(entries){
  var rows = [['Číslo auditu','Pracoviště','Provozovna','Druh auditu','Auditor','Zúčastněné osoby','Datum uzavření','Výsledek','Úspěšnost %','OK','NOK','N/A','Neshody (zjištění)','Dobrá praxe','Nepřítomní']];
  entries.forEach(function(e){
    var a = e[1], pct = auditScorePercent(a);
    rows.push([auditNoValue(a), a.workplaceName||'', a.branchName||'Sídlo firmy', a.auditTypeName||'', a.auditorName||'',
      auditPresentNames(a).join(', '), fmtDate(a.closedAt), auditUsesOkNok(a) ? ((a.summary&&a.summary.overall)||'') : '',
      (pct!=null && auditUsesPercent(a))?pct:'', (a.summary&&a.summary.ok)||0, (a.summary&&a.summary.nok)||0, (a.summary&&a.summary.na)||0,
      (a.findings||[]).filter(function(f){ return f.result==='NOK'; }).length, (a.findings||[]).filter(function(f){ return f.result==='GOOD'; }).length, auditAbsentNames(a).join(', ')]);
  });
  return rows;
}
// Řádek za každé NOK (otázka i podotázka) s opatřením a jeho aktuálním stavem.
function historyCsvNokRows(entries){
  var rows = [['ID opatření','Číslo auditu','Datum auditu','Pracoviště','Provozovna','Druh auditu','Auditor','Skupina','Otázka','Podotázka','Zjištění','Nápravné opatření','Odpovědná osoba','Termín','Stav opatření','Po termínu']];
  entries.forEach(function(e){
    var id = e[0], a = e[1], codeSeqs = auditCorrectiveSeqMap(a);
    (a.answers||[]).forEach(function(ans){
      function push(sa){
        var src = sa || ans, item = buildCorrectiveItem(id, a, ans, sa, src.correctiveAction || {});
        var open = item.status!=='hotovo' && item.status!=='zruseno';
        rows.push([correctiveCode(a, codeSeqs[sa ? ans.id+'::'+sa.id : ans.id]), auditNoValue(a), fmtDate(a.closedAt), a.workplaceName||'', a.branchName||'Sídlo firmy', a.auditTypeName||'', a.auditorName||'',
          ans.groupName||'', ans.questionText||'', sa ? (sa.text||'') : '', src.note||'', item.description, item.responsible,
          item.dueDate ? fmtDateShort(item.dueDate) : '', CORRECTIVE_STATUS_LABELS[item.status]||item.status, (open && isCorrectiveOverdue(item)) ? 'ano' : 'ne']);
      }
      if(ans.result==='NOK') push(null);
      (ans.subAnswers||[]).forEach(function(sa){ if(sa.result==='NOK') push(sa); });
    });
    // Neshody z volných zjištění (vlastní pracoviště, místo otázky „Volné zjištění“).
    (a.findings||[]).forEach(function(f){
      if(f.result!=='NOK') return;
      var item = buildCorrectiveItem(id, a, f, null, f.correctiveAction || {});
      var open = item.status!=='hotovo' && item.status!=='zruseno';
      rows.push([correctiveCode(a, codeSeqs[f.id]), auditNoValue(a), fmtDate(a.closedAt), item.workplaceName||'', item.branchName||'Sídlo firmy', a.auditTypeName||'', a.auditorName||'',
        'Volné zjištění', '', '', f.note||'', item.description, item.responsible,
        item.dueDate ? fmtDateShort(item.dueDate) : '', CORRECTIVE_STATUS_LABELS[item.status]||item.status, (open && isCorrectiveOverdue(item)) ? 'ano' : 'ne']);
    });
  });
  return rows;
}
// Hromadné PDF bylo zrušeno (rozhodnutí uživatele) — PDF jen pro jednotlivý audit/opatření.
// kind: 'csv' | 'csvnok'; scope: 'selected' (zaškrtnuté) | 'shown' (vše podle filtrů)
App.exportHistory = async function(kind, scope){
  if(!downloadsCap){ showToast('Stahování souborů není v tomto zobrazení dostupné.'); return; }
  var entries = filteredHistoryList();
  if(scope==='selected') entries = entries.filter(function(e){ return state.historySelectedIds.has(e[0]); });
  if(!entries.length){ showToast('Žádné audity k exportu.'); return; }
  var stamp = new Date().toISOString().slice(0,10);
  try{
    var rows = kind==='csvnok' ? historyCsvNokRows(entries) : historyCsvSummaryRows(entries);
    if(kind==='csvnok' && rows.length===1){ showToast('Vybrané audity nemají žádné NOK.'); return; }
    var res = await downloadsCap.save({filename: (kind==='csvnok'?'audity_nok_opatreni_':'audity_souhrn_')+stamp+'.csv', data: csvBlob(rows)});
    if(res && res.status==='saved') showToast('CSV uloženo.');
  }catch(e){
    if(e && e.code==='declined'){ /* zrušeno */ }
    else { console.error('history export failed', e); showToast('Export se nezdařil.'); }
  }
};

function renderNoAccess(){
  document.getElementById('content').innerHTML = '<div class="empty">'+ICONS.empty+
    '<p>K této části nemáte oprávnění.</p></div>';
}

export {
  appLogoPngForPdf,
  ensurePdfLibs,
  MONTH_NAMES_CS,
  csvBlob,
  historyMoreMenuHtml,
  renderNoAccess
};
